import * as React from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { visuallyHidden } from '@mui/utils';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Collapse from '@mui/material/Collapse';
import { Button } from '@mui/material';

function descendingComparator<Data>(a: Data, b: Data, orderBy: keyof Data) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof Data>(
    order: Order,
    orderBy: Key,
): (
    a: { [key in Key]: string },
    b: { [key in Key]: string },
) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort<T>(array: readonly Data[], comparator: (a: Data, b: Data) => number) {
    const stabilizedThis = array.map((el, index) => [el, index] as [Data, number]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
        return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
    disablePadding: boolean;
    id: keyof Data;
    label: string;
    numeric: boolean;
}
interface ClientHeadCell {
    disablePadding: boolean;
    id: keyof ClientData;
    label: string;
    numeric: boolean;
}
interface Data {
    amount: string;
    createdAt: string;
    currency: string;
    id: string;
    note: string;
    reference: string | null;
    settledAt: string | null;
    status: string;
    updatedAt: string;
    userId: string;
}
// interface ClientData {
//     email: string;
//     nickname: string;
//     cad_balance: string;
//     usd_balance: string;
//     usdc_wallet: string;
//     createdAt: string;
// }
// interface WithdrawalData {
//     amount: string;
//     createdAt: string;
//     currency: string;
//     id: string;
//     note: string;
//     settledAt: string | null;
//     status: string;
//     updatedAt: string;
//     userId: string;
// }
interface ClientData {
    Withdrawals: Array<Data>;
    cad_balance: number;
    createdAt: string;
    email: string;
    id: string;
    nickname: string;
    updatedAt: string;
    usd_balance: number;
    usdc_wallet: string;
    usdt_wallet: string;
}
const clientHeadCells: readonly ClientHeadCell[] = [
    {
        id: 'email',
        numeric: false,
        disablePadding: true,
        label: 'User',
    },
    {
        id: 'nickname',
        numeric: false,
        disablePadding: true,
        label: 'User Name',
    },
    {
        id: 'cad_balance',
        numeric: false,
        disablePadding: true,
        label: 'CAD Balance',
    },
    {
        id: 'usd_balance',
        numeric: false,
        disablePadding: true,
        label: 'USD Balance',
    },
    {
        id: 'usdc_wallet',
        numeric: false,
        disablePadding: true,
        label: 'Wallet',
    },
    {
        id: 'createdAt',
        numeric: false,
        disablePadding: true,
        label: 'Created At',
    },
]
const headCells: readonly HeadCell[] = [
    {
        id: 'createdAt',
        numeric: false,
        disablePadding: true,
        label: 'Created',
    },
    {
        id: 'id',
        numeric: false,
        disablePadding: true,
        label: 'Request ID',
    },
    {
        id: 'amount',
        numeric: true,
        disablePadding: false,
        label: 'amount',
    },
    {
        id: 'note',
        numeric: true,
        disablePadding: false,
        label: 'note',
    },
    {
        id: 'currency',
        numeric: true,
        disablePadding: false,
        label: 'currency',
    },
    {
        id: 'status',
        numeric: true,
        disablePadding: false,
        label: 'status',
    },
    {
        id: 'settledAt',
        numeric: true,
        disablePadding: false,
        label: 'Settled',
    },
];

interface EnhancedTableProps {
    numSelected: number;
    onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
    onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    order: Order;
    orderBy: keyof Data;
    rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
    const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } =
        props;
    const createSortHandler =
        (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
            onRequestSort(event, property);
        };

    return (
        <TableHead>
        <TableRow>
            <TableCell padding="checkbox">
            <Checkbox
                color="primary"
                indeterminate={numSelected > 0 && numSelected < rowCount}
                checked={rowCount > 0 && numSelected === rowCount}
                onChange={onSelectAllClick}
                inputProps={{
                'aria-label': 'select all desserts',
                }}
            />
            </TableCell>
            {headCells.map((headCell) => (
            <TableCell
                key={headCell.id}
                align={headCell.numeric ? 'right' : 'left'}
                padding={headCell.disablePadding ? 'none' : 'normal'}
                sortDirection={orderBy === headCell.id ? order : false}
            >
                <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
                >
                {headCell.label}
                {orderBy === headCell.id ? (
                    <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                ) : null}
                </TableSortLabel>
            </TableCell>
            ))}
        </TableRow>
        </TableHead>
    );
}
function ClientTableHead(){
    return (
        <TableHead>
        <TableRow>
            <TableCell/>
            {clientHeadCells.map((headCell) => (
                <TableCell
                    key={headCell.id}
                    align={headCell.numeric ? 'right' : 'center'}
                    padding={headCell.disablePadding ? 'none' : 'normal'}
                >
                    {headCell.label}
                </TableCell>
            ))}
        </TableRow>
        </TableHead>
    );
}
interface EnhancedTableToolbarProps {
    numSelected: number;
}

function EnhancedTableToolbar(props: EnhancedTableToolbarProps) {
    const { numSelected } = props;

    return (
        <Toolbar
        sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            ...(numSelected > 0 && {
            bgcolor: (theme) =>
                alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
            }),
        }}
        >
        {numSelected > 0 ? (
            <Typography
            sx={{ flex: '1 1 100%' }}
            color="inherit"
            variant="subtitle1"
            component="div"
            >
            {numSelected} selected
            </Typography>
        ) : (
            <Typography
            sx={{ flex: '1 1 100%' }}
            variant="h6"
            id="tableTitle"
            component="div"
            >
            Nutrition
            </Typography>
        )}
        {numSelected > 0 ? (
            <Tooltip title="Delete">
            <IconButton>
                <DeleteIcon />
            </IconButton>
            </Tooltip>
        ) : (
            <Tooltip title="Filter list">
            <IconButton>
                <FilterListIcon />
            </IconButton>
            </Tooltip>
        )}
        </Toolbar>
    );
}
function Row(props: { row: ReturnType<any>, handleSelectRequest: any, isAdmin: boolean }) {
    const { row, handleSelectRequest, isAdmin } = props;
    const [open, setOpen] = React.useState(false);

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
            <TableCell>
                <IconButton
                    aria-label="expand row"
                    size="small"
                    onClick={() => setOpen(!open)}
                >
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
            </TableCell>
            <TableCell component="th" scope="row">
                {row.email}
            </TableCell>
            <TableCell align="right">{row.nickname}</TableCell>
            <TableCell align="right">{row.cad_balance}</TableCell>
            <TableCell align="right">{row.usd_balance}</TableCell>
            <TableCell align="right">{row.usdc_wallet}</TableCell>
            <TableCell align="right">{row.createdAt}</TableCell>
            </TableRow>
            <TableRow>
            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                <Collapse in={open} timeout="auto" unmountOnExit>
                <Box sx={{ margin: 1 }}>
                    <Typography variant="h6" gutterBottom component="div">
                        Requests
                    </Typography>
                    <Table size="small" aria-label="purchases">
                    <TableHead>
                        <TableRow>
                            <TableCell>Created</TableCell>
                            <TableCell>Request ID</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="right">Note</TableCell>
                            <TableCell align="right">Currency</TableCell>
                            <TableCell align="right">Status</TableCell>
                            <TableCell align="right">Settled</TableCell>
                            <TableCell align="right">Reference</TableCell>
                            <TableCell align="right">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {row.Withdrawals.map((withdrawal: Data) => (
                        <TableRow key={withdrawal.id}>
                            <TableCell component="th" scope="row">{withdrawal.createdAt}</TableCell>
                            <TableCell>{withdrawal.id}</TableCell>
                            <TableCell align="right">{withdrawal.amount}</TableCell>
                            <TableCell align="right">{withdrawal.note}</TableCell>
                            <TableCell align="right">{withdrawal.currency}</TableCell>
                            <TableCell 
                                align="right" 
                                style={withdrawal.status === 'canceled' ? {color: 'red'} : withdrawal.status === 'complete' ? {color: 'green'}: undefined}>{withdrawal.status}</TableCell>
                            <TableCell align="right">{withdrawal.settledAt}</TableCell>
                            <TableCell align="right">{withdrawal.reference}</TableCell>
                            <TableCell align="right">
                                <Button disabled={withdrawal.status !== 'pending' || !isAdmin} onClick={() => handleSelectRequest(withdrawal)}>Update</Button>
                            </TableCell>
                        </TableRow>
                        ))}
                    </TableBody>
                    </Table>
                </Box>
                </Collapse>
            </TableCell>
            </TableRow>
        </React.Fragment>
    );
}

export default function WithdrawalTable(props: { data : ClientData[], update: any, setUpdate: any, handleSelectRequestToUpdate: any, isAdmin: boolean}) {
    const { data, update, setUpdate, handleSelectRequestToUpdate, isAdmin } = props;
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof Data>('createdAt');
    const [selected, setSelected] = React.useState<readonly string[]>([]);
    const [page, setPage] = React.useState(0);
    const [dense, setDense] = React.useState(false);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);

    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: keyof Data,
    ) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
        const newSelected = data.map((n: any) => n.id);
        setSelected(newSelected);
        return;
        }
        setSelected([]);
    };

    const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected: readonly string[] = [];

        if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
            selected.slice(0, selectedIndex),
            selected.slice(selectedIndex + 1),
        );
        }

        setSelected(newSelected);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDense(event.target.checked);
    };

    const isSelected = (name: string) => selected.indexOf(name) !== -1;

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

    return (
        <Box sx={{ width: '100%' }}>
        <Paper sx={{ width: '100%', mb: 2 }}>
            {/* <EnhancedTableToolbar numSelected={selected.length} /> */}
            <TableContainer sx={{ width: '100%' }}>
            <Table
                sx={{ width: '100%' }}
                aria-labelledby="tableTitle"
                size={'medium'}
            >
                <ClientTableHead />
                {/* <EnhancedTableHead
                    numSelected={selected.length}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleSelectAllClick}
                    onRequestSort={handleRequestSort}
                    rowCount={data.length}
                /> */}
                <TableBody>
                {data && data
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                    const isItemSelected = isSelected(row.id);
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                        <Row row={row} handleSelectRequest={handleSelectRequestToUpdate} isAdmin={isAdmin}/>
                    )})}

                </TableBody>
            </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={data.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
        {/* <FormControlLabel
            control={<Switch checked={dense} onChange={handleChangeDense} />}
            label="Dense padding"
        /> */}
        </Box>
    );
}